import { Button } from "@/components/ui/button";
import { Body, Superscript } from "@hypercard/web-components";
import { Card } from "@/components/ui/card";
import { IconBox } from "@common/components";
import {
  Airplane,
  CaretRight,
  CurrencyDollar,
  ForkKnife,
  Heart,
} from "@phosphor-icons/react";
import { CreateStipendStep } from "./types";
import { AnimatedProps, animated } from "@react-spring/web";
import { CSSProperties } from "react";
import { useResetRecoilState } from "recoil";
import { createOrEditStipendState } from "@common/state/createOrEditStipendState";

type Props = AnimatedProps<{
  onNext: (step: CreateStipendStep) => void;
  style: CSSProperties;
}>;

export const CreateNewStipend = ({ onNext, style }: Props) => {
  const resetCreateOrEditStipendState = useResetRecoilState(
    createOrEditStipendState,
  );
  const handleCreateStipend = () => {
    resetCreateOrEditStipendState();
    onNext(CreateStipendStep.BaseForm);
  };
  const handleSelectTemplate = () => {
    onNext(CreateStipendStep.SelectTemplate);
  };
  return (
    <animated.div
      style={{ opacity: style.opacity }}
      className="flex w-full grow flex-col items-center justify-center gap-y-10 pl-6 lg:px-28"
    >
      <Body size="2xl" weight="medium">
        Create New Stipend
      </Body>
      <div className="flex flex-col justify-center gap-4 md:flex-row">
        <Card className="flex max-w-[440px] flex-1 flex-col items-center px-6 py-12">
          <div className="rounded bg-success/5">
            <IconBox className="shrink-0">
              <CurrencyDollar weight="bold" className="size-8 text-success" />
            </IconBox>
          </div>
          <Superscript size="md" className="mt-10 text-white/70">
            New Stipend
          </Superscript>
          <Body size="xl" weight="medium" className="mt-5 text-center">
            Build Your Own Stipend
          </Body>
          <Body
            size="md"
            weight="regular"
            className="mt-2 text-center text-white/70"
          >
            Tailor a stipend to meet your company’s unique needs. Start from
            scratch and customize every detail.
          </Body>
          <Button
            size="lg"
            onClick={handleCreateStipend}
            className="mt-10 w-full"
          >
            <span>Create Stipend</span>
            <CaretRight weight="bold" className="ml-2 size-3" />
          </Button>
        </Card>
        <Card className="flex max-w-[440px] flex-1 flex-col items-center px-6 py-12">
          <div className="flex gap-x-5">
            <IconBox className="shrink-0">
              <Heart weight="bold" className="size-8" />
            </IconBox>
            <IconBox className="shrink-0">
              <Airplane weight="bold" className="size-8" />
            </IconBox>
            <IconBox className="shrink-0">
              <ForkKnife weight="bold" className="size-8" />
            </IconBox>
          </div>
          <Superscript size="md" className="mt-10 text-white/70">
            Template
          </Superscript>
          <Body size="xl" weight="medium" className="mt-5 text-center">
            Use Our Stipend Templates
          </Body>
          <Body
            size="md"
            weight="regular"
            className="mt-2 text-center text-white/70"
          >
            Get started quickly by choosing from our pre-designed templates.
            Just select and customize.
          </Body>
          <Button
            size="lg"
            onClick={handleSelectTemplate}
            className="mt-10 w-full"
          >
            <span>Select Template</span>
            <CaretRight weight="bold" className="ml-2 size-3" />
          </Button>
        </Card>
      </div>
      <Body
        size="md"
        weight="regular"
        className="mt-2 text-center text-white/70"
      >
        Stipends are not considered business expenses or reimbursable costs.
        They function as taxable income, which can be easily exported through
        Hypercard and uploaded into payroll software. Unlike reimbursable
        expenses, which may have specific requirements—such as certain meal
        criteria—to qualify for reimbursement and be claimed on an IRS tax
        return, stipends are subject to tax implications that companies must
        manage appropriately. We rely on companies to determine how stipends are
        utilized and ensure proper tax reporting.
      </Body>
    </animated.div>
  );
};
